/**
 * The three calls the enrichment page makes, for all nine entities.
 *
 * Same plumbing as `use-artist-repository.tsx`: `useApiUrl()` plus Refine's
 * `custom()`, which appends the `?api-version=1.0` query and carries the
 * Firebase ID token through the shared axios interceptor. **No hook here builds
 * a header or a raw `fetch`** — nothing in the repo does, and `custom()` adds a
 * trailing slash that every enrichment endpoint tolerates (design C.11.9).
 */
import { useApiUrl, useDataProvider, useNotification } from "@refinedev/core";
import { z } from "zod";

import { notifyMutationError } from "@/lib/repository-utils";
import { parseRead } from "@/lib/zod/read-schema";

import { type EnrichmentPageConfig, enrichmentConfigFor } from "./config";
import type { EnrichBody, TranslationsBody } from "./save-bodies";

/**
 * The read half of the read/write policy: lenient, and consumed through
 * `parseRead`, which hands back the raw payload when the schema misses. One
 * unexpected enrichment key must not blank the page.
 */
const lenientRecordSchema = z.object({}).passthrough();

export type EnrichmentRecord = Record<string, unknown>;

export interface EnrichmentRepository {
  /** The entity's config, so the page does not look it up twice. */
  config: EnrichmentPageConfig;
  /** The parent entity document. Throws — the page owns the load toast. */
  get: (id: string) => Promise<EnrichmentRecord>;
  /** `PUT {apiUrl}/{path}/enrich`. `true` on success. */
  saveEnrichment: (body: EnrichBody) => Promise<boolean>;
  /** `PUT {apiUrl}/{path}/enrichment-translations`. `true` on success. */
  saveTranslations: (body: TranslationsBody) => Promise<boolean>;
}

export function useEnrichmentRepository(
  resource: string,
): EnrichmentRepository {
  const config = enrichmentConfigFor(resource);
  const apiUrl = useApiUrl();
  const { custom, getOne } = useDataProvider()();
  const { open } = useNotification();

  async function get(id: string): Promise<EnrichmentRecord> {
    const response = await getOne({ id, resource: config.resource });
    return parseRead(lenientRecordSchema, response, config.resource);
  }

  /**
   * Both saves report through `notifyMutationError`, whose description is
   * `toHumanErrorMessage(error)`. For an axios failure that prefers
   * `error.response.data.message`, which is exactly where the API puts the
   * first error of a `Result.Fail` — every domain failure is a **400 with a
   * message body**, never a 404, so the message is the only signal there is.
   */
  async function saveEnrichment(body: EnrichBody): Promise<boolean> {
    try {
      await custom?.({
        url: `${apiUrl}/${config.path}/enrich`,
        method: "put",
        payload: body,
      });
      return true;
    } catch (error) {
      notifyMutationError(open, "Save enrichment", error);
      return false;
    }
  }

  async function saveTranslations(body: TranslationsBody): Promise<boolean> {
    try {
      await custom?.({
        url: `${apiUrl}/${config.path}/enrichment-translations`,
        method: "put",
        payload: body,
      });
      return true;
    } catch (error) {
      notifyMutationError(open, "Save translations", error);
      return false;
    }
  }

  return { config, get, saveEnrichment, saveTranslations };
}
